// src/pages/admin/JobReportPage.jsx
// Job report page showing all candidates for a job - standardized header

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trackPageView, trackReportViewed } from '../../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.invenire.app';

export default function JobReportPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('score');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    document.title = 'Invenire - Job Report';
    trackPageView(`/admin/jobs/${jobId}/report`, 'Job Report');
    trackReportViewed('job', jobId);
  }, [jobId]);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/scoring/jobs/${jobId}/report`);
      
      if (!response.ok) {
        throw new Error('Failed to load job report');
      }
      
      const data = await response.json();
      setJob(data.job);
      setCandidates(data.candidates || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const sortedCandidates = [...candidates].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortBy) {
      case 'score':
        aVal = a.overall_score || 0;
        bVal = b.overall_score || 0;
        break;
      case 'name':
        aVal = a.candidate_name || '';
        bVal = b.candidate_name || '';
        return sortOrder === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      case 'date':
        aVal = new Date(a.completed_at || a.created_at || 0);
        bVal = new Date(b.completed_at || b.created_at || 0);
        break;
      case 'recommendation':
        const recOrder = { 'strong_yes': 4, 'yes': 3, 'maybe': 2, 'no': 1 };
        aVal = recOrder[a.recommendation] || 0;
        bVal = recOrder[b.recommendation] || 0;
        break;
      default:
        aVal = 0;
        bVal = 0;
    }
    
    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const getScoreColor = (score) => {
    if (!score && score !== 0) return '#6b7280';
    if (score >= 7) return '#22c55e';
    if (score >= 5) return '#3b82f6';
    if (score >= 3) return '#eab308';
    return '#ef4444';
  };

  const getRecommendationStyle = (rec) => {
    switch (rec) {
      case 'strong_yes':
        return { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', label: 'Strong Yes' };
      case 'yes':
        return { bg: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', label: 'Yes' };
      case 'maybe':
        return { bg: 'rgba(234, 179, 8, 0.2)', color: '#eab308', label: 'Maybe' };
      case 'no':
        return { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', label: 'No' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280', label: 'Pending' };
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', label: 'Completed' };
      case 'in_progress':
        return { bg: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', label: 'In Progress' };
      case 'paused':
        return { bg: 'rgba(234, 179, 8, 0.2)', color: '#eab308', label: 'Paused' };
      case 'invited':
        return { bg: 'rgba(168, 85, 247, 0.2)', color: '#a855f7', label: 'Invited' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280', label: status || 'Unknown' };
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Loading job report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h2>Failed to Load Report</h2>
          <p style={{ color: '#ef4444' }}>{error}</p>
          <button onClick={loadReport} style={styles.retryButton}>Retry</button>
          <button onClick={() => navigate('/admin/reports')} style={styles.backLink}>
            ← Back to Reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire" 
            style={styles.logoImage}
          />
          <span style={styles.subtitle}>Job Report</span>
        </div>
        <button onClick={() => navigate('/admin/reports')} style={styles.backButton}>
          ← Back to Reports
        </button>
      </div>

      <div style={styles.content}>
        {/* Job Info */}
        {job && (
          <div style={styles.jobHeader}>
            <h1 style={styles.jobTitle}>{job.title}</h1>
            {job.company_name && (
              <p style={styles.jobCompany}>{job.company_name}</p>
            )}
            <div style={styles.jobStats}>
              <span style={styles.stat}>
                <strong>{candidates.length}</strong> candidates
              </span>
              <span style={styles.stat}>
                <strong>{candidates.filter(c => c.status === 'completed').length}</strong> completed
              </span>
              <span style={styles.stat}>
                <strong>{candidates.filter(c => c.overall_score).length}</strong> scored
              </span>
            </div>
          </div>
        )}

        {/* Sort Controls */}
        <div style={styles.controls}>
          <div style={styles.sortGroup}>
            <label style={styles.sortLabel}>Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              style={styles.select}
            >
              <option value="score">Score</option>
              <option value="recommendation">Recommendation</option>
              <option value="name">Name</option>
              <option value="date">Date</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              style={styles.sortButton}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          <button onClick={loadReport} style={styles.refreshButton}>
            ↻ Refresh
          </button>
        </div>

        {/* Candidates List */}
        {sortedCandidates.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No candidates found for this job.</p>
            <button 
              onClick={() => navigate('/admin/candidates')}
              style={styles.primaryButton}
            >
              Add Candidates
            </button>
          </div>
        ) : (
          <div style={styles.candidatesList}>
            {sortedCandidates.map((candidate, index) => {
              const recStyle = getRecommendationStyle(candidate.recommendation);
              const statusStyle = getStatusBadge(candidate.status);
              
              return (
                <div 
                  key={candidate.interview_id || index}
                  style={styles.candidateCard}
                  onClick={() => candidate.interview_id && navigate(`/admin/interviews/${candidate.interview_id}/report`)}
                >
                  <div style={styles.candidateRank}>
                    #{index + 1}
                  </div>
                  
                  <div style={styles.candidateInfo}>
                    <div style={styles.candidateName}>
                      {candidate.candidate_name || 'Unknown'}
                    </div>
                    {candidate.candidate_email && (
                      <div style={styles.candidateEmail}>
                        {candidate.candidate_email}
                      </div>
                    )}
                  </div>
                  
                  <div style={styles.candidateScore}>
                    {candidate.overall_score ? (
                      <span style={{
                        ...styles.scoreCircle,
                        borderColor: getScoreColor(candidate.overall_score),
                        color: getScoreColor(candidate.overall_score)
                      }}>
                        {candidate.overall_score.toFixed(1)}
                      </span>
                    ) : (
                      <span style={styles.noScore}>—</span>
                    )}
                  </div>
                  
                  <div style={styles.candidateRec}>
                    <span style={{
                      ...styles.recBadge,
                      backgroundColor: recStyle.bg,
                      color: recStyle.color
                    }}>
                      {recStyle.label}
                    </span>
                  </div>
                  
                  <div style={styles.candidateStatus}>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: statusStyle.bg,
                      color: statusStyle.color
                    }}>
                      {statusStyle.label}
                    </span>
                  </div>
                  
                  <div style={styles.candidateAction}>
                    {candidate.interview_id && (
                      <span style={styles.viewLink}>View →</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)',
    color: '#fff',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    color: '#9ca3af'
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#a855f7',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: 40
  },
  retryButton: {
    marginTop: 16,
    padding: '10px 20px',
    background: '#a855f7',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer'
  },
  backLink: {
    marginTop: 12,
    background: 'none',
    border: 'none',
    color: '#60a5fa',
    cursor: 'pointer',
    fontSize: 14
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 40px',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  logoImage: {
    height: 48,
    width: 'auto',
    objectFit: 'contain'
  },
  subtitle: {
    fontSize: 14,
    color: '#9ca3af'
  },
  backButton: {
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer'
  },
  content: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '32px 24px'
  },
  jobHeader: {
    marginBottom: 32
  },
  jobTitle: {
    fontSize: 28,
    fontWeight: 700,
    margin: '0 0 8px 0'
  },
  jobCompany: {
    fontSize: 16,
    color: '#9ca3af',
    margin: '0 0 16px 0'
  },
  jobStats: {
    display: 'flex',
    gap: 24
  },
  stat: {
    fontSize: 14,
    color: '#9ca3af'
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24
  },
  sortGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  sortLabel: {
    fontSize: 14,
    color: '#9ca3af'
  },
  select: {
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6,
    color: '#fff',
    fontSize: 14
  },
  sortButton: {
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer',
    fontSize: 14
  },
  refreshButton: {
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer',
    fontSize: 14
  },
  emptyState: {
    textAlign: 'center',
    padding: 60,
    background: 'rgba(255,255,255,0.02)',
    borderRadius: 12,
    color: '#9ca3af'
  },
  primaryButton: {
    marginTop: 16,
    padding: '12px 24px',
    background: '#a855f7',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600
  },
  candidatesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  candidateCard: {
    display: 'grid',
    gridTemplateColumns: '50px 2fr 80px 120px 100px 60px',
    alignItems: 'center',
    padding: '16px 20px',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.2s, border-color 0.2s'
  },
  candidateRank: {
    fontSize: 14,
    fontWeight: 600,
    color: '#6b7280'
  },
  candidateInfo: {},
  candidateName: {
    fontWeight: 600,
    color: '#fff'
  },
  candidateEmail: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2
  },
  candidateScore: {
    textAlign: 'center'
  },
  scoreCircle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: '2px solid',
    fontSize: 16,
    fontWeight: 700
  },
  noScore: {
    color: '#6b7280',
    fontSize: 20
  },
  candidateRec: {
    textAlign: 'center'
  },
  recBadge: {
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500
  },
  candidateStatus: {
    textAlign: 'center'
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500
  },
  candidateAction: {
    textAlign: 'right'
  },
  viewLink: {
    color: '#60a5fa',
    fontSize: 13
  }
};
